//@flow
import memoize from 'memoizee';
import GlobalData from '../../Utils/globalData';
import { toJS } from '../../Utils/immutable';

import type { HandlerFunction } from './functionStoreClass';
import type BaseStore, { StoreState } from './immutableStoreClass';
import type APIResponseReader from '../../Utils/api-response-reader';

export function getServerDefaultData(key:string):?Object {
  const data = GlobalData.get('apiData', key);
  return (data && data.body) || data;
}

export function applyServerDefaultData(key:string, state:StoreState, modifier:(StoreState, Object) => any) {
  const resp = getServerDefaultData(key);
  if (resp) modifier(state, resp);
}

// returns an action handler function that will set the flag to a constant value
export function setFlag(flag:string, value:boolean):HandlerFunction {
  return updateState((state:StoreState) => setFlagProp(state, flag, value));
}

// returns an action handler that will wrap the provided function in a store
// update call to use as an action handler
export function updateState(modifier:(StoreState, any) => any):HandlerFunction {
  return (store:BaseStore, data:any) => {
    store._updateState((storeData:StoreState) => {
      modifier(storeData, data);
    });
  };
}

export function emitChange(store:BaseStore) {
  store.__emitChange();
}

export function reset(store:BaseStore) {
  store._reset();
}

// returns an action handler that combines updateState with a getResponseBody
// call since that's a fairly common use case
export function apiUpdateState(modifier:(StoreState, any) => any):HandlerFunction {
  return updateState((data:StoreState, resp:APIResponseReader) => {
    modifier(data, resp && resp.getResponseBody());
  });
}

export function createDataObject<T:Object>(DataType:Class<T>, data:?Object):T {
  return new DataType(toJS(data));
}

export const cachedCreateObjectFromData = memoize(createDataObject);

export function loadDataObject<T:Object>(state:StoreState, path:string|string[], creator:(any) => T, loadFunc:?() => any):T {
  const data = state.getIn([].concat(path));

  if (!data && loadFunc) loadFunc();

  return creator(data);
}

export function loadImmutableData<T:Object>(state:StoreState, path:string|string[], loadFunc:?() => any, defaultVal:T):T {
  const data = state.getIn([].concat(path));

  if (!data && loadFunc) loadFunc();

  return data || defaultVal;
}

// utility function used by ImmutableStore to get flags.  this one is
// useful if you want to do getFlag calls without using the store
export function getFlagProp(state:StoreState, flag:string):boolean {
  return state.getIn([ 'flags', flag ]) || false;
}

// utility function used by ImmutableStore to set flags.  this one is
// useful if you want to set multiple flags in an update function
export function setFlagProp(state:StoreState, flag:string, value:boolean):StoreState {
  return state.setIn(['flags', flag], value);
}

// utility function used by ImmutableStore to set flags.  this one is
// useful if you want to set multiple flags in an update function
export function clearFlagProp(state:StoreState, flag:string):StoreState {
  return state.deleteIn(['flags', flag]);
}
